import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AccountService } from '../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { ScheduledBooking } from '../../../../../../models/scheduled-booking';
import { ScheduledBookingService } from '../../../../../../services/scheduled-booking/scheduled-booking.service';
import { capitalizeFirstLetter, timeStringFromISO8601 } from '../../../../../../utils';

enum refundOptions {
  noRefund,
  partialRefund,
  fullRefund
}

/**
 * A component handling cancellation and refund of reservations on a booking.
 */
@Component({
  selector: 'app-manage-booking-cancellation-and-refund',
  templateUrl: './manage-booking-cancellation-and-refund.component.html',
  styleUrls: ['./manage-booking-cancellation-and-refund.component.css']
})
export class ManageBookingCancellationAndRefundComponent implements OnInit {

  // The selected reservation. This reservation will be initially selected for
  // cancellation, though the user is able to then select any or none of the
  // reservations on the booking.
  public reservationId: number;
  public bookingId: number;
  public booking: ScheduledBooking;

  public loadingBooking = false;
  public loadingSubmit = false;

  public errorMessage = '';

  public serverError: number = null;

  public reservationIdsToCancel: number[] = [];
  public refundSelection: refundOptions = refundOptions.noRefund;
  public refundCurrencyAmount = 0;
  public refundVoucherCount = 0;
  public sendCancellationEmails = true;
  public cancellationEmailText = '';

  public refundOptions = refundOptions;

  public capitalizeFirstLetter = capitalizeFirstLetter;
  public timeStringFromISO8601 = timeStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private activatedRouteService: ActivatedRoute,
    private router: Router,
    private bookingService: ScheduledBookingService
  ) {}

  ngOnInit() {
    const routeParams = this.activatedRouteService.snapshot.params;
    this.bookingId = parseInt(routeParams.bookingId, 10);
    this.reservationId = parseInt(routeParams.reservationId, 10);
    if (isNaN(this.bookingId) || isNaN(this.reservationId)) {
      this.navigateToEventList();
    }
    this.reservationIdsToCancel.push(this.reservationId);

    this.loadBooking();
  }

  public loadBooking() {
    this.loadingBooking = true;

    this.bookingService
      .getScheduledBooking(this.bookingId)
      .then(booking => {
        this.booking = booking;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.navigateToEventList();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingBooking = false; });
  }

  public onSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';

    if (this.refundSelection === refundOptions.noRefund) {
      this.refundCurrencyAmount = 0;
      this.refundVoucherCount = 0;
    } else if (this.refundSelection === refundOptions.fullRefund) {
      this.refundCurrencyAmount = this.booking.total_currency_amount - this.booking.refunded_currency_amount;
      this.refundVoucherCount = this.booking.total_num_vouchers - this.booking.refunded_num_vouchers;
    }

    this.bookingService
      .manageBookingCancellationAndRefund(
        this.bookingId,
        this.reservationIdsToCancel,
        this.refundCurrencyAmount,
        this.refundVoucherCount,
        this.sendCancellationEmails,
        this.cancellationEmailText
      )
      .then(() => {
        // TODO: Display confirmation text
        this.navigateToEventList();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;

          // Reload the booking because its info may have changed.
          this.loadBooking();
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to manage this booking.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public getBackLink() {
    return [this.router.url.split('/booking')[0]];
  }

  public isLoading() {
    return this.loadingBooking || this.loadingSubmit;
  }

  public selectAllReservationsToCancel() {
    this.reservationIdsToCancel = this.booking.active_reservations.map(
      (res) => res.id
    );
  }

  public toggleCancelReservation(reservationId: number) {
    const idx = this.reservationIdsToCancel.indexOf(reservationId, 0);
    if (idx >= 0) {
      this.reservationIdsToCancel.splice(idx, 1);
    } else {
      this.reservationIdsToCancel.push(reservationId);
    }
  }

  private navigateToEventList() {
    this.router.navigate(this.getBackLink());
  }

}
